/**
 * Consent as the event contract wants it.
 *
 * Mirrors `consentSchema` in @quizee/events by hand, including its two cross field rules:
 * `storage_allowed` implies a granted status, and a `tcf` source always carries the TC string.
 * Building an object that breaks either of those would be rejected at ingest, so the rules are
 * enforced here at the point of construction rather than hoped for.
 */
export interface Consent {
  readonly status: 'granted' | 'denied' | 'unknown';
  readonly source: 'tcf' | 'host' | 'none';
  readonly storage_allowed: boolean;
  readonly purposes?: number[] | undefined;
  readonly tcf_string?: string | undefined;
}

/**
 * The safe default: no CMP on the page means no storage, and no claim either way about what the
 * reader wants. A poll still renders and still accepts a vote in this state.
 */
export const NO_CONSENT: Consent = {
  status: 'unknown',
  source: 'none',
  storage_allowed: false,
};

/** TCF purpose 1 is "store and access information on a device". Nothing else gates storage. */
const STORAGE_PURPOSE = 1;
const MAX_TC_STRING = 2048;

export type TcfApi = (
  command: 'getTCData',
  version: 2,
  callback: (tcData: unknown, success: boolean) => void,
) => void;

export interface TcfWindow {
  __tcfapi?: TcfApi | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Purpose ids the reader has consented to, filtered to the range the event schema accepts. */
function consentedPurposes(tcData: Record<string, unknown>): number[] {
  const purpose = tcData['purpose'];
  if (!isRecord(purpose)) return [];
  const consents = purpose['consents'];
  if (!isRecord(consents)) return [];

  const ids: number[] = [];
  for (const key of Object.keys(consents)) {
    if (consents[key] !== true) continue;
    const parsed = Number(key);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 11) continue;
    ids.push(parsed);
  }
  return ids.sort((a, b) => a - b);
}

/**
 * Read a TCF data payload defensively.
 *
 * Anything we cannot make sense of falls back to no consent. A CMP that is still booting, has
 * errored, or hands back a TC string we cannot carry within the schema's limits is treated the
 * same as a page with no CMP at all.
 */
export function consentFromTcData(tcData: unknown): Consent {
  if (!isRecord(tcData)) return NO_CONSENT;

  const cmpStatus = tcData['cmpStatus'];
  if (cmpStatus === 'error' || cmpStatus === 'stub' || cmpStatus === 'loading') return NO_CONSENT;

  const tcString = tcData['tcString'];
  if (typeof tcString !== 'string' || tcString.length === 0) return NO_CONSENT;
  // Truncating would produce a string that decodes to something other than what the reader agreed
  // to, which is worse than recording no consent signal at all.
  if (tcString.length > MAX_TC_STRING) return NO_CONSENT;

  const purposes = consentedPurposes(tcData);
  const storageAllowed = purposes.includes(STORAGE_PURPOSE);

  return {
    status: storageAllowed ? 'granted' : 'denied',
    source: 'tcf',
    storage_allowed: storageAllowed,
    purposes,
    tcf_string: tcString,
  };
}

/**
 * Ask the host page's CMP, with a deadline.
 *
 * A CMP that never calls back must not hold the widget hostage, so the timeout resolves to the
 * safe default rather than leaving the reader looking at a skeleton.
 */
export function resolveConsent(win: TcfWindow, timeoutMs = 1500): Promise<Consent> {
  return new Promise<Consent>((resolve) => {
    const api = win.__tcfapi;
    if (typeof api !== 'function') {
      resolve(NO_CONSENT);
      return;
    }

    let settled = false;
    /**
     * Deliberately let rather than const, and declared before `done` uses it.
     *
     * `done` closes over the timer to clear it, so the binding has to exist before `done` is
     * defined. Declared this way it is simply undefined until the timeout is armed, and the guard
     * inside `done` copes. As a const initialised further down it would throw on any path that
     * resolved before the assignment, which is a trap for whoever reorders this next.
     */
    // eslint-disable-next-line prefer-const
    let timer: ReturnType<typeof setTimeout> | undefined;

    const done = (consent: Consent): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve(consent);
    };

    timer = setTimeout(() => done(NO_CONSENT), timeoutMs);

    try {
      api('getTCData', 2, (tcData, success) => {
        done(success === true ? consentFromTcData(tcData) : NO_CONSENT);
      });
    } catch {
      done(NO_CONSENT);
    }
  });
}
